// ============================================================================
// COMMANDPALETTE.JS - Paleta de comandos global (Cmd/Ctrl+K)
// ============================================================================
// Se abre con Cmd+K (Mac) / Ctrl+K (Win/Linux) desde cualquier pantalla
// autenticada. Cierra con Esc, clic-fuera o al elegir una acción.
//
// Acciones:
//  1. Saltar a secciones (Tareas, Documentación, Usuarios/Config solo-admin,
//     Perfil, Histórico, y Crawl/Google Ads si el addon está activo).
//  2. Crear tarea.
//  3. Saltar a una tarea concreta filtrando por REF (CERTI-123) o por nombre
//     (case/acento-insensible), máx. 8 resultados.
//
// SOLO LECTURA del estado global: lee `tasks` del TaskStore (Zustand) y los
// addons de clientConfigService igual que TaskApp/AddonTabs. No muta nada.
// El listener global de teclado ignora el atajo si el foco está en un
// input/textarea/contenteditable, para no robar Cmd+K mientras se escribe.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { m, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Search,
  ListChecks,
  BookOpen,
  Users,
  Settings,
  User,
  History,
  Globe,
  BarChart3,
  Plus,
  FileText,
  CornerDownLeft,
  Sparkles,
  HeartPulse,
  X,
} from 'lucide-react';

import { useAuth } from '../auth/AuthContext';
import { useTaskStore } from '../store/TaskStore';
import clientConfigService from '../services/clientConfigService';
import { getTaskRef, getTaskPath } from '../utils/taskRef';
import '../styles/command-palette.css';

// Normaliza a minúsculas y sin acentos para comparar (case/acento-insensible).
const norm = (s) =>
  (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

// ¿El elemento con foco es editable? (para no secuestrar Cmd+K mientras se escribe)
const isEditable = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
};

function CommandPalette() {
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  // SOLO LECTURA: nos suscribimos a las tareas ya cargadas por TaskApp.
  const tasks = useTaskStore((s) => s.tasks);
  const reduceMotion = useReducedMotion();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const openRef = useRef(false);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // Addons activos del tenant. Misma fuente que AddonTabs/TaskApp
  // (clientConfigService); ausencia => todos activos (default documentado).
  const [addons, setAddons] = useState(
    () => clientConfigService.getConfig()?.addons || { crawl: true, gads: true }
  );
  useEffect(() => {
    const apply = (cfg) => setAddons(cfg?.addons || { crawl: true, gads: true });
    apply(clientConfigService.getConfig());
    const unsubscribe = clientConfigService.subscribe(apply);
    return unsubscribe;
  }, []);

  const admin = typeof isAdmin === 'function' ? isAdmin() : false;

  // ── Listener global del atajo (solo con sesión) ─────────────────────────
  useEffect(() => {
    if (!token) return undefined;

    const onKeyDown = (e) => {
      const isK = e.key === 'k' || e.key === 'K';
      const mod = e.metaKey || e.ctrlKey;
      if (isK && mod) {
        if (openRef.current) {
          // Abierto: Cmd+K lo cierra (toggle).
          e.preventDefault();
          setOpen(false);
        } else if (!isEditable(document.activeElement)) {
          // Cerrado y no escribiendo en otro campo: abrir.
          e.preventDefault();
          setOpen(true);
        }
        return;
      }
      if (openRef.current && e.key === 'Escape') {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [token]);

  // Al abrir: limpiar búsqueda, resetear resaltado, enfocar input y bloquear scroll.
  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setActiveIndex(0);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(id);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const close = () => setOpen(false);
  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  // ── Construcción de items (secciones + acciones + tareas) ───────────────
  const { sections, flat } = useMemo(() => {
    const q = norm(query.trim());

    const navItems = [
      { key: 'tareas', label: 'Tareas', sub: 'Ir al tablero', icon: ListChecks, kw: 'tablero dashboard inicio home', path: '/task' },
      { key: 'docs', label: 'Documentación', sub: 'Biblioteca de documentos', icon: BookOpen, kw: 'docs documentacion biblioteca guias', path: '/task/documentation' },
      admin && { key: 'users', label: 'Usuarios', sub: 'Gestionar equipo', icon: Users, kw: 'usuarios equipo gestionar miembros', path: '/task/users' },
      admin && { key: 'config', label: 'Configuración', sub: 'Configuración de cliente', icon: Settings, kw: 'configuracion ajustes cliente modulos addons', path: '/task/client-config' },
      { key: 'profile', label: 'Perfil', sub: 'Mi cuenta', icon: User, kw: 'perfil mi cuenta usuario', path: '/task/profile' },
      { key: 'history', label: 'Histórico de sprints', sub: 'Sprints cerrados', icon: History, kw: 'historico archivo sprints cerrados', path: '/task/sprint-history' },
      addons.crawl && { key: 'crawl', label: 'Crawl SEO', sub: 'Dashboard de rastreo', icon: Globe, kw: 'crawl rastreo seo dashboard', path: '/task/crawl' },
      addons.gads && { key: 'gads', label: 'Google Ads', sub: 'Panel de campañas', icon: BarChart3, kw: 'google ads sem panel campanas', path: '/task/gads-panel' },
      addons.changelog && { key: 'changelog', label: 'Changelog', sub: 'Historial de cambios de Google Ads', icon: History, kw: 'changelog historial cambios log google ads', path: '/task/changelog' },
      addons.aivis && { key: 'aivis', label: 'Visibilidad IA', sub: 'Foto de visibilidad en buscadores de IA', icon: Sparkles, kw: 'visibilidad ia aivis chatgpt gemini buscadores', path: '/task/aivis' },
      addons.onesearch && { key: 'onesearch', label: 'OneSearch', sub: 'Salud de marca en Google', icon: HeartPulse, kw: 'onesearch marca brand salud search console ads', path: '/task/onesearch' },
    ].filter(Boolean);

    const actionItems = [
      { key: 'create', label: 'Crear tarea', sub: 'Nueva tarea', icon: Plus, kw: 'crear nueva tarea añadir add', path: '/task/create' },
    ];

    const cmdMatch = (it) => !q || norm(`${it.label} ${it.kw || ''}`).includes(q);

    const navMatches = navItems.filter(cmdMatch).map((it) => ({
      key: it.key,
      icon: it.icon,
      label: it.label,
      sub: it.sub,
      run: () => go(it.path),
    }));

    const actionMatches = actionItems.filter(cmdMatch).map((it) => ({
      key: it.key,
      icon: it.icon,
      label: it.label,
      sub: it.sub,
      run: () => go(it.path),
    }));

    // Tareas: solo al teclear algo. Filtra por REF (CERTI-123) o nombre.
    const taskMatches = q
      ? (Array.isArray(tasks) ? tasks : [])
          .filter((t) => norm(getTaskRef(t)).includes(q) || norm(t?.nombre).includes(q))
          .slice(0, 8)
          .map((t) => ({
            key: `task-${t.id}`,
            icon: FileText,
            label: t.nombre || '(sin título)',
            ref: getTaskRef(t),
            run: () => {
              setOpen(false);
              navigate(getTaskPath(t));
            },
          }))
      : [];

    // Índice global correlativo para la navegación con teclado.
    let gi = 0;
    const secs = [];
    const pushSection = (label, items) => {
      if (!items.length) return;
      secs.push({
        label,
        items: items.map((it) => ({ ...it, gi: gi++ })),
      });
    };
    pushSection('Ir a', navMatches);
    pushSection('Acciones', actionMatches);
    pushSection('Tareas', taskMatches);

    const flatItems = secs.flatMap((s) => s.items);
    return { sections: secs, flat: flatItems };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, admin, addons, tasks]);

  // Mantener el resaltado dentro de rango al cambiar los resultados.
  useEffect(() => {
    setActiveIndex((i) => (flat.length ? Math.min(i, flat.length - 1) : 0));
  }, [flat.length]);

  // Asegurar que el item resaltado queda visible al navegar con teclado.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector(`[data-gi="${activeIndex}"]`);
    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flat.length) setActiveIndex((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flat.length) setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flat[activeIndex];
      if (item) item.run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  const overlayDur = reduceMotion ? 0 : 0.12;
  const panelDur = reduceMotion ? 0 : 0.16;

  return (
    <AnimatePresence>
      {open && (
        <m.div
          data-motion-fallback
          className="cmdk-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: overlayDur }}
          onMouseDown={(e) => {
            // Clic fuera de la tarjeta cierra.
            if (e.target === e.currentTarget) close();
          }}
        >
          <m.div
            data-motion-fallback
            className="cmdk-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Paleta de comandos"
            initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.98, y: reduceMotion ? 0 : -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: reduceMotion ? 1 : 0.98, y: reduceMotion ? 0 : -6 }}
            transition={{ duration: panelDur, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="cmdk-search">
              <Search size={18} className="cmdk-search-icon" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                className="cmdk-input"
                placeholder="Buscar o saltar a…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={onInputKeyDown}
                aria-label="Buscar o saltar a"
                autoComplete="off"
                spellCheck="false"
              />
              <span className="cmdk-esc">esc</span>
              {/* Táctil: sin tecla Esc, cierre de 44 px (el CSS lo muestra solo
                  sin hover y oculta el chip «esc»). */}
              <button
                type="button"
                className="cmdk-close"
                onClick={close}
                aria-label="Cerrar paleta de comandos"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="cmdk-list" ref={listRef} role="listbox" aria-label="Resultados">
              {flat.length === 0 ? (
                <div className="cmdk-empty">No hay resultados para “{query}”.</div>
              ) : (
                sections.map((section) => (
                  <div key={section.label} className="cmdk-group">
                    <div className="cmdk-group-label">{section.label}</div>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = item.gi === activeIndex;
                      return (
                        <button
                          key={item.key}
                          type="button"
                          data-gi={item.gi}
                          role="option"
                          aria-selected={isActive}
                          className={`cmdk-item${isActive ? ' is-active' : ''}`}
                          onMouseMove={() => setActiveIndex(item.gi)}
                          onClick={() => item.run()}
                        >
                          <Icon size={16} className="cmdk-item-icon" aria-hidden="true" />
                          <span className="cmdk-item-body">
                            <span className="cmdk-item-label">{item.label}</span>
                            {item.sub && <span className="cmdk-item-sub">{item.sub}</span>}
                          </span>
                          {item.ref && <span className="cmdk-item-ref">{item.ref}</span>}
                          <CornerDownLeft size={14} className="cmdk-item-enter" aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            <div className="cmdk-footer">
              <span className="cmdk-footer-hint">
                <span className="cmdk-key">↑</span>
                <span className="cmdk-key">↓</span>
                navegar
              </span>
              <span className="cmdk-footer-hint">
                <span className="cmdk-key">↵</span>
                abrir
              </span>
              <span className="cmdk-footer-hint">
                <span className="cmdk-key">esc</span>
                cerrar
              </span>
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}

export default CommandPalette;
